'use client';

import { useCallback } from 'react';

import { useCurrentUserId } from '@ps/ui/contexts/auth';

import { useCommunityContext } from '../contexts/community/useCommunityContext';
import { toggleMessageReaction } from '../functions/message/toggleMessageReaction';
import type { MessageReaction } from '../types/MessageReaction';

/**
 * Toggle a reaction on the given (channelId, messageId). The hook
 * resolves `creatorId` and `userId` from auth context so callers
 * only pass the reaction details.
 *
 * Returns a no-op when there's no signed-in user — pre-auth callers
 * shouldn't reach this path, but bailing keeps the toggle handler
 * safe for the auth-restoring window.
 */
export function useToggleMessageReaction({
  channelId,
  messageId,
}: {
  channelId: string | null | undefined;
  messageId: string | null | undefined;
}) {
  const { creatorId } = useCommunityContext();
  const currentUserId = useCurrentUserId();
  return useCallback(
    async (reaction: MessageReaction) => {
      if (!creatorId || !currentUserId || !channelId || !messageId) return;
      await toggleMessageReaction({
        creatorId,
        channelId,
        messageId,
        reaction,
      });
    },
    [creatorId, currentUserId, channelId, messageId],
  );
}
